import { resources } from "@constants/hardcoded-strings";
import { ServiceTemplatesReferenceData, SERVICE_TEMPLATES_REFERENCE_DATA_TYPES } from "@constants/reference-data";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ServiceTemplatesTableContainer } from "@containers/management-console/service-templates/service-templates-table-container/service-templates-table-container";
import { AddServiceTemplateModal } from "@containers/modals/add-service-template-modal/add-service-template-modal";
import { useModalContext } from "@context/use-modal-context";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ModalTypes } from "@enums/modal-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { ApplicationState } from "@idw-types/state/application-state";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getServiceTemplatesReduxRequest } from "@redux-requests/service-templates/get-service-templates";
import { getActiveReferenceData } from "@utils/data-util";
import { toObject } from "@utils/object-util";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export const ServiceTemplatesContainer = () => {
    const modalContext = useModalContext()

    const dispatch = useDispatch();
    const {
        isLoadingServiceTemplates,
        formattedServiceTemplates,
        serviceTemplatesTableState
    } = useSelector((state: ApplicationState) => ({
        isLoadingServiceTemplates: state.serviceTemplates.data.isLoading,
        formattedServiceTemplates: state.serviceTemplates.data.serviceTemplates,
        serviceTemplatesTableState: state.tableSettings.serviceTemplates
    }));

    const [shouldLoadServiceTemplates, setShouldLoadServiceTemplates] = useState(true);
    const referenceData = useReferenceDataSelector<ServiceTemplatesReferenceData>(SERVICE_TEMPLATES_REFERENCE_DATA_TYPES);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.workflowTemplates && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                SERVICE_TEMPLATES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.SERVICE_TEMPLATES
            );
        }
    }, [shouldLoadReferenceData, referenceData.isLoading.serviceTemplates]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies
        );
    }, [
        referenceData.axnCompanies
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingServiceTemplates && shouldLoadServiceTemplates) {
            setShouldLoadServiceTemplates(false);
            getServiceTemplatesReduxRequest(dispatch)(referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingServiceTemplates, shouldLoadServiceTemplates]);

    const serviceTemplatesMap = useMemo(() => {
        if (formattedServiceTemplates?.length > 0) {
            return toObject<RpServiceTemplate>(formattedServiceTemplates, 'name');
        }

        return {};
    }, [formattedServiceTemplates]);

    const {
        axnCompanies: activeAxnCompanies
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnCompanies } = referenceData;

            return getActiveReferenceData({ axnCompanies });
        }

        return {
            axnCompanies: []
        };
    }, [isReferenceDataPresent, referenceData.axnCompanies]);


    const renderAddServiceTemplate = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddServiceTemplateModal
                serviceTemplatesMap={serviceTemplatesMap}
                axnCompanies={activeAxnCompanies}
                onModalClose={onModalClose}
            />
        );
    }, [serviceTemplatesMap, activeAxnCompanies]);

    const isLoading = useMemo(() => {
        return (
            isLoadingServiceTemplates ||
            referenceData.isLoading.serviceTemplates
        );
    }, [
        isLoadingServiceTemplates,
        referenceData.isLoading.serviceTemplates
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            formattedServiceTemplates &&
            !shouldLoadServiceTemplates
        );
    }, [
        isLoading,
        formattedServiceTemplates,
        shouldLoadServiceTemplates
    ]);


    const headerButton = useMemo(() => {
        const onClick = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddServiceTemplate,
                renderFunction: renderAddServiceTemplate
            });
        };
        return (
            <ButtonWithIcon
                id="add-service-template-button"
                title={resources.pages.serviceTemplates.headerButton}
                text={resources.pages.serviceTemplates.headerButton}
                onClick={onClick}
                icon={faPlusCircle}
                disabled={isLoading || !isLoaded}
            />
        );
    }, [isLoading, isLoaded, renderAddServiceTemplate]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.serviceTemplates.header}
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <ServiceTemplatesTableContainer
                        serviceTemplates={formattedServiceTemplates}
                        tableState={serviceTemplatesTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
}